import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageService } from 'src/app/_services/storage.service';

@Component({
  selector: 'app-integration-guide',
  templateUrl: './integration-guide.component.html',
  styleUrls: ['./integration-guide.component.css']
})
export class IntegrationGuideComponent implements OnInit, OnDestroy {
  isLoggedIn: boolean = false;
  docId: string = '';
  docContent: any[] = [];
  openContainers: Set<number> = new Set();
  copiedIndex: number | null = null;
  pageTitle: string = '';
  activeHeaderId: string = '';
  docNotFound: boolean = false;
  lightboxImage: { src: string; alt: string } | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private readonly tableHeadersCache = new WeakMap<object, string[]>();
  private readonly tableRowsCache = new WeakMap<object, string[][]>();
  private menuOrderCache: any[] | null = null;
  private menuOrderSource: string | null = null;

  get isPayoutDoc(): boolean {
    return this.docId === 'payout' || this.docId === 'payout-apis' || this.docId === 'payout-bulk';
  }

  get hasHero(): boolean {
    return this.docContent.some(item => typeof item?.content === 'string'
      && item.content.includes('doc-hero'));
  }

  get timelineHeaders(): any[] {
    // Must return the ORIGINAL docContent objects, not new ones. This getter runs on every
    // change-detection pass; mapping to fresh objects gives *ngFor a new identity each time,
    // so Angular tears down and rebuilds every row and the click never lands.
    return this.docContent.filter(item =>
      item.id && (item.type === 'header' || item.type === 'note-info'));
  }

  timelineLabel(item: any): string {
    return item?.type === 'note-info' ? item.header : item.content;
  }

  trackTimeline(_index: number, item: any): string {
    return item.id;
  }

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly storageService: StorageService
  ) {
    this.isLoggedIn = this.storageService.isLoggedIn();
  }

  ngOnInit(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      const newDocId = params.get('doc');
      if (!newDocId) {
        this.docId = '';
        this.docContent = [];
        return;
      }
      this.loadDocument(newDocId);
    });
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  navigateToSignup(): void {
    this.router.navigate(['/auth/login']);
  }

  navigateToFirstMenuItem(): void {
    const items = this.sortedMenuItems;
    if (!items.length) return;
    const first = items[0];
    if (first?.nodeInfo?.menuLink) {
      localStorage.setItem('selectedMenu', first.nodeName);
      this.router.navigate([first.nodeInfo.menuLink], { queryParams: first.nodeInfo.queryParams || {} });
    }
  }

  get sortedMenuItems(): any[] {
    const data = localStorage.getItem('menuItemsFormattedIntegrationGuide');
    if (!data) {
      this.menuOrderSource = null;
      this.menuOrderCache = null;
      return [];
    }
    if (this.menuOrderSource === data && this.menuOrderCache) {
      return this.menuOrderCache;
    }

    this.menuOrderSource = data;
    this.menuOrderCache = this.flattenMenuTree(JSON.parse(data));
    return this.menuOrderCache;
  }

  // Depth-first order matching how the sidebar renders the tree
  private flattenMenuTree(items: any[]): any[] {
    const idSet = new Set(items.map(item => item.nodeId));
    const childrenByParent = new Map<number, any[]>();
    const roots: any[] = [];

    items.forEach(item => {
      if (item.parentId != null && idSet.has(item.parentId)) {
        const siblings = childrenByParent.get(item.parentId) ?? [];
        siblings.push(item);
        childrenByParent.set(item.parentId, siblings);
      } else {
        roots.push(item);
      }
    });

    const byPosition = (a: any, b: any) =>
      Number(a.nodeInfo?.position ?? 0) - Number(b.nodeInfo?.position ?? 0);

    roots.sort(byPosition);
    childrenByParent.forEach(siblings => siblings.sort(byPosition));

    const ordered: any[] = [];
    const visit = (node: any) => {
      ordered.push(node);
      (childrenByParent.get(node.nodeId) ?? []).forEach(visit);
    };
    roots.forEach(visit);

    return ordered;
  }

  get currentMenuIndex(): number {
    return this.sortedMenuItems.findIndex(
      item => item.nodeInfo?.queryParams?.doc === this.docId
    );
  }

  get prevMenuItem(): any | null {
    const items = this.sortedMenuItems;
    const idx = this.currentMenuIndex;
    if (idx <= 0) return null;

    for (let i = idx - 1; i >= 0; i--) {
      if (items[i]?.nodeInfo?.menuLink) return items[i];
    }
    return null;
  }

  get nextMenuItem(): any | null {
    const items = this.sortedMenuItems;
    const idx = this.currentMenuIndex;
    if (idx < 0) return null;

    for (let i = idx + 1; i < items.length; i++) {
      if (items[i]?.nodeInfo?.menuLink) return items[i];
    }
    return null;
  }

  navigateToPrev(): void {
    const item = this.prevMenuItem;
    if (!item) return;
    localStorage.setItem('selectedMenu', item.nodeName);
    this.router.navigate([item.nodeInfo.menuLink], { queryParams: item.nodeInfo.queryParams || {} });
  }

  navigateToNext(): void {
    const item = this.nextMenuItem;
    if (!item) return;
    localStorage.setItem('selectedMenu', item.nodeName);
    this.router.navigate([item.nodeInfo.menuLink], { queryParams: item.nodeInfo.queryParams || {} });
  }

  highlightCode(content: string, language?: string): string {
    if (language !== 'curl') return content;
    return content.replace(/(<[^>]+>)|([^<]+)/g, (_match: string, tag: string, plain: string) => {
      if (tag) return tag;
      return plain.replace(
        /(\bcurl\b)|(--[\w-]+)|('(?:[^'\\]|\\.)*')|(\\.)/g,
        (m: string, cmd: string, flag: string, str: string, bs: string) => {
          if (cmd) return `<span class="hl-cmd">${cmd}</span>`;
          if (flag) return `<span class="hl-flag">${flag}</span>`;
          if (str) return `<span class="hl-str">${str}</span>`;
          if (bs) return `<span class="hl-bs">${bs}</span>`;
          return m;
        }
      );
    });
  }

  private loadDocument(docId: string): void {
    this.docId = docId;
    this.openContainers.clear();
    this.docContent = [];
    this.activeHeaderId = '';
    this.docNotFound = false;
    this.pageTitle = localStorage.getItem('selectedMenu') || '';
    console.debug('Loading document:', docId);
    fetch(`assets/integration-guide-content/${docId}.json`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Document not found: ${docId}`);
        }
        return res.json();
      })
      .then((data: any[]) => {
        this.docContent = data;
        setTimeout(() => this.setupIntersectionObserver(), 150);
      })
      .catch(err => {
        console.error('Failed to load document:', err);
        this.docNotFound = true;
      });
  }

  private setupIntersectionObserver(): void {
    this.intersectionObserver?.disconnect();
    const scrollContainer = document.querySelector('.integration-hero.doc-view') as HTMLElement;
    if (!scrollContainer) return;

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeHeaderId = entry.target.id;
          }
        }
      },
      { root: scrollContainer, rootMargin: '-10% 0px -75% 0px', threshold: 0 }
    );

    document.querySelectorAll('.doc-header[id], .doc-note[id]').forEach(el => {
      this.intersectionObserver!.observe(el);
    });
  }

  scrollToHeader(id: string): void {
    const el = document.getElementById(id);
    const container = document.querySelector('.integration-hero.doc-view') as HTMLElement;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      container.scrollTop += elRect.top - containerRect.top - 40;
    }
  }

  scrollToTop(): void {
    const container = document.querySelector('.integration-hero.doc-view') as HTMLElement;
    if (container) {
      container.scrollTop = 0;
      this.activeHeaderId = '';
    }
  }

  columnWidth(item: any, index: number): string | null {
    const widths = item?.columnWidths;
    return Array.isArray(widths) ? (widths[index] ?? null) : null;
  }

  getTableHeaders(item: any): string[] {
    const cached = this.tableHeadersCache.get(item);
    if (cached) return cached;

    const headers = (item.content as string[]).slice(0, item.columns);
    this.tableHeadersCache.set(item, headers);
    return headers;
  }

  getTableRows(item: any): string[][] {
    const cached = this.tableRowsCache.get(item);
    if (cached) return cached;

    const result: string[][] = [];
    for (let i = 1; i < item.rows; i++) {
      result.push((item.content as string[]).slice(i * item.columns, (i + 1) * item.columns));
    }
    this.tableRowsCache.set(item, result);
    return result;
  }

  toggleContainer(index: number): void {
    if (this.openContainers.has(index)) {
      this.openContainers.delete(index);
    } else {
      this.openContainers.add(index);
    }
  }

  isContainerOpen(index: number): boolean {
    return this.openContainers.has(index);
  }

  openLightbox(src: string, alt: string): void {
    this.lightboxImage = { src, alt: alt || '' };
  }

  closeLightbox(): void {
    this.lightboxImage = null;
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.lightboxImage) {
      this.closeLightbox();
    }
  }

  copyCode(content: string, index: number): void {
    const text = content.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
    navigator.clipboard.writeText(text).then(() => {
      this.copiedIndex = index;
      setTimeout(() => { this.copiedIndex = null; }, 2000);
    });
  }
test(){
  alert('test');
}
}
